import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useEffect } from "react";
import { 
  DollarSign, 
  Music, 
  Users, 
  Star, 
  Headphones, 
  TrendingUp, 
  Shield, 
  CheckCircle,
  ArrowRight,
  Sparkles,
  Wrench,
  ListMusic,
  ExternalLink,
  Zap,
  Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";

import deadWordsLogo from "@/assets/dead-words-logo.png";
import deadAirLogo from "@/assets/dead-air-logo.png";
import deadMastersLogo from "@/assets/dead-masters-logo.png";
import navSkullZebra from "@/assets/nav-skull-zebra.png";

export default function WhatWeOffer() {
  const navigate = useNavigate();

  // SEO meta tags
  useEffect(() => {
    document.title = "What We Offer | Soundope - Music Promotion & Feedback Platform";
    
    const metaTags = [
      { name: "description", content: "Discover what Soundope offers: paid music reviews, honest feedback, playlist pitching to 100s of viral playlists, and premium artist tools. $1 for 30+ reviews. Unbeatable value." },
      { name: "keywords", content: "music promotion, paid music reviews, playlist pitching, artist feedback, music marketing, Soundope features" },
      { property: "og:title", content: "What We Offer | Soundope" },
      { property: "og:description", content: "Get paid to review music. Get honest feedback on your tracks. Pitch to 100s of viral playlists. All the tools independent artists need." },
    ];

    metaTags.forEach(tag => {
      const selector = tag.name ? `meta[name="${tag.name}"]` : `meta[property="${tag.property}"]`;
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement("meta");
        if (tag.name) meta.name = tag.name;
        if (tag.property) meta.setAttribute("property", tag.property);
        document.head.appendChild(meta);
      }
      meta.content = tag.content;
    });
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      {/* Hero Section */}
      <section className="px-4 py-12 md:py-20 text-center max-w-5xl mx-auto">
        <div className="flex justify-center mb-6">
          <img src={navSkullZebra} alt="" className="w-20 h-20 object-contain" />
        </div>
        <h1 className="text-4xl md:text-6xl font-masking-renta text-foreground mb-4">
          What We Offer
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          Everything independent artists need to grow. Real feedback, real playlists, real results.
        </p>
      </section>

      {/* Get Paid to Review Section */}
      <section className="px-4 py-12 bg-[#1a1a1a]/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground">
              Get Paid to Leave Feedback
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-muted-foreground text-lg mb-4">
                Turn your ear into income. Listen to new music, share your honest opinion, and earn real money. 
                Every review you leave puts cash in your pocket while helping fellow artists improve.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Earn 1¢ per review submitted</span>
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Cash out via PayPal when you hit $15</span>
                </li>
                <li className="flex items-center gap-3 text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Discover dope new music while you earn</span>
                </li>
              </ul>
            </div>
            <div className="neuro-base rounded-2xl p-6">
              <h3 className="text-xl font-bold text-foreground mb-3">Why People Love It</h3>
              <p className="text-muted-foreground">
                No ads, no gimmicks. Just real humans giving real feedback. 
                Artists get quality insights, reviewers get paid. Win-win.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Feedback for Artists Section */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
              <Headphones className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground">
              Honest Feedback for Artists
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="neuro-base rounded-2xl p-6 border-2 border-primary/30">
              <div className="text-4xl font-masking-renta text-primary mb-2">$1</div>
              <div className="text-lg font-bold text-foreground mb-2">For Over 30 Reviews</div>
              <p className="text-muted-foreground mb-4">
                Unbeatable value. Upload your track, get dozens of real opinions. 
                Know exactly what's working and what needs work.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>Real listeners, not bots</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>Anonymous feedback = brutal honesty</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span>First upload is FREE</span>
                </li>
              </ul>
            </div>
            <div>
              <p className="text-muted-foreground text-lg mb-4">
                Stop wondering if your music hits. Get real feedback from real people. 
                Our anonymous review system ensures you get the truth—no sugarcoating.
              </p>
              <Button 
                onClick={() => navigate(createPageUrl('Upload'))}
                className="bg-primary hover:bg-primary/80 text-white"
              >
                Upload Your First Track Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Playlist Pitching Section */}
      <section className="px-4 py-12 bg-[#1a1a1a]/50">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <ListMusic className="w-6 h-6 text-cyan-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground">
              Playlist Pitching Packages
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <p className="text-muted-foreground text-lg mb-4">
                Skip the grind. We've built relationships with 100s of active, viral playlists across every genre. 
                When you pitch through us, real curators actually listen.
              </p>
              <div className="neuro-base rounded-2xl p-6">
                <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  Never Any Bots. Guaranteed.
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>100s of vetted, active playlist curators</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Real streams from real fans</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>Viral potential playlists included</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>We pitch for you—just submit</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <div className="text-center neuro-base rounded-2xl p-8">
                <h3 className="text-2xl font-masking-renta text-foreground mb-2">Ready to Get Playlisted?</h3>
                <p className="text-muted-foreground mb-4">
                  Use credits to pitch your tracks to curators who actually care about discovering new music.
                </p>
                <Button 
                  onClick={() => navigate(createPageUrl('Submit'))}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  Submit to Playlists
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Pitch Section - We Pitch For You */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500/30 to-orange-500/30 flex items-center justify-center">
              <Crown className="w-6 h-6 text-yellow-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground">
              Premium Pitch — We Do It For You
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="neuro-base rounded-2xl p-6 border-2 border-yellow-500/30 relative overflow-hidden">
              <div className="absolute top-3 right-3">
                <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                  DONE FOR YOU
                </span>
              </div>
              <div className="flex items-baseline gap-2 mb-3 mt-4">
                <span className="text-4xl font-masking-renta text-yellow-400">$15</span>
                <span className="text-muted-foreground text-sm">one-time</span>
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3">
                We Pitch to 100+ Curators
              </h3>
              <p className="text-muted-foreground mb-4">
                Too busy to pitch yourself? Hand it off to us. We'll submit your track to our entire network of 100+ active playlist curators.
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span>100+ curators across all genres</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span>Just drop your Spotify link</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span>We handle the rest</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <span>Real placements, no bots</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/premium-pitch')}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-bold"
              >
                Get Premium Pitch
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="flex flex-col justify-center">
              <h3 className="text-xl font-bold text-foreground mb-4">Why Premium Pitch?</h3>
              <p className="text-muted-foreground text-lg mb-6">
                Pitching takes time. Finding curators, writing personalized messages, following up—it's a grind. 
                With Premium Pitch, you skip all that. We've already built the relationships. 
                You just provide the music.
              </p>
              <div className="neuro-base rounded-xl p-4 border border-yellow-500/20">
                <p className="text-sm text-muted-foreground italic">
                  "I used to spend hours pitching to playlists with no response. 
                  Premium Pitch got me on 4 playlists in my first week."
                </p>
                <p className="text-xs text-muted-foreground/70 mt-2">— Actual Soundope user</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Tools Section */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-purple-400" />
            </div>
            <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground">
              Premium Tools That Sweep the Competition
            </h2>
          </div>
          <p className="text-muted-foreground text-lg mb-8">
            We're not just a feedback platform. We've built an entire suite of tools that make other services look ancient.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* Dead Words */}
            <a
              href="https://deadwords.us"
              target="_blank"
              rel="noopener noreferrer"
              className="neuro-base rounded-2xl p-6 hover:scale-[1.02] transition-transform group cursor-pointer"
            >
              <div className="flex justify-center mb-4">
                <img src={deadWordsLogo} alt="Dead Words" className="h-12 object-contain" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2 text-center group-hover:text-purple-400 transition-colors">Dead Words</h3>
              <p className="text-sm text-muted-foreground text-center mb-3">
                AI-powered lyric videos. Turn your words into visuals that slap.
              </p>
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground group-hover:text-purple-400 transition-colors">
                <span>deadwords.us</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            </a>

            {/* Dead Masters */}
            <a
              href="https://deadmasters.us"
              target="_blank"
              rel="noopener noreferrer"
              className="neuro-base rounded-2xl p-6 hover:scale-[1.02] transition-transform group cursor-pointer"
            >
              <div className="flex justify-center mb-4">
                <img src={deadMastersLogo} alt="Dead Masters" className="h-12 object-contain" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2 text-center group-hover:text-purple-400 transition-colors">Dead Masters</h3>
              <p className="text-sm text-muted-foreground text-center mb-3">
                Professional-grade mastering without the professional price tag.
              </p>
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground group-hover:text-purple-400 transition-colors">
                <span>deadmasters.us</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            </a>

            {/* Dead Air */}
            <a
              href="https://deadair.us"
              target="_blank"
              rel="noopener noreferrer"
              className="neuro-base rounded-2xl p-6 hover:scale-[1.02] transition-transform group cursor-pointer"
            >
              <div className="flex justify-center mb-4">
                <img src={deadAirLogo} alt="Dead Air" className="h-12 object-contain" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2 text-center group-hover:text-cyan-400 transition-colors">Dead Air</h3>
              <p className="text-sm text-muted-foreground text-center mb-3">
                Crosspost to all your socials with one click.
              </p>
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground group-hover:text-cyan-400 transition-colors">
                <span>deadair.us</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            </a>
          </div>

          <div className="text-center">
            <Button 
              onClick={() => navigate(createPageUrl('Tools'))}
              variant="outline"
              className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
            >
              Explore All Tools
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Credits CTA Section */}
      <section className="px-4 py-12 bg-gradient-to-b from-primary/10 to-transparent">
        <div className="max-w-3xl mx-auto text-center">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl md:text-4xl font-masking-renta text-foreground mb-4">
            Ready to Level Up?
          </h2>
          <p className="text-lg text-muted-foreground mb-6">
            Get credits and unlock everything Soundope has to offer. 
            Feedback, playlists, and tools—all at your fingertips.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Different behavior for native vs web */}
            {typeof window !== 'undefined' && window.Capacitor ? (
              <Button 
                onClick={() => {
                  import('sonner').then(({ toast }) => {
                    toast.info(
                      "Credits are how you upload, submit to playlists, and more. But we don't sell credits on mobile. Please visit our web store to get credits.\n\nsoundope.com/shop",
                      { duration: 6000 }
                    );
                  });
                }}
                className="bg-primary hover:bg-primary/80 text-white px-8 py-6 text-lg"
              >
                Get Credits
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={() => navigate(createPageUrl('Shop'))}
                className="bg-primary hover:bg-primary/80 text-white px-8 py-6 text-lg"
              >
                Get Credits
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            <Button 
              onClick={() => navigate(createPageUrl('Submit'))}
              variant="outline"
              className="border-muted-foreground/30 text-foreground hover:bg-muted/50 px-8 py-6 text-lg"
            >
              Submit a Track
            </Button>
          </div>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-masking-renta text-foreground mb-8 text-center">
            Why We're Different
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Real Humans Only</h3>
              <p className="text-sm text-muted-foreground">
                No bots. No fake engagement. Every interaction is from a real person.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-cyan-400" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Built for Growth</h3>
              <p className="text-sm text-muted-foreground">
                Every feature is designed to help independent artists succeed.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-4">
                <Music className="w-8 h-8 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-2">Underground First</h3>
              <p className="text-sm text-muted-foreground">
                We champion the underground. No gatekeeping, just good music.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
